import { Check, Copy, Loader2 } from "lucide-react";
import posthog from "posthog-js";
import { useState } from "react";
import { cn } from "../../handlers/cn.utils";
import {
	CLAIMABLE_COMPLETION_PCT,
	referralInviteMessage,
	referralRefusalMessage,
	whatsappLink,
} from "../../handlers/rewards";
import { isValidEmail, isValidPhoneNumberWithTZCode } from "../../handlers/validationFunctions";
import { useCreateReferral } from "../../services/create-resume/rewardQueries";
import { Alert, AlertDescription } from "../Alert/Alert";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import WhatsAppIcon from "./WhatsAppIcon";

interface ReferFriendModalProps {
	open: boolean;
	onClose: () => void;
}

interface Candidate {
	fullName: string;
	emailAddress: string;
	phoneNumber: string;
}

type FieldName = keyof Candidate;

const REWARD_VALUE = 150;

/** WhatsApp brand green, which exists in no scale of ours, so it is written out rather than themed. */
const WHATSAPP_BUTTON = "bg-[#25D366] text-white hover:bg-[#1DA851] active:bg-[#128C7E] focus:ring-[#25D366]";

const EMPTY: Candidate = { fullName: "", emailAddress: "", phoneNumber: "" };

function validate(candidate: Candidate): Partial<Record<FieldName, string>> {
	const errors: Partial<Record<FieldName, string>> = {};

	if (candidate.fullName.trim().split(/\s+/).filter(Boolean).length < 2) {
		errors.fullName = candidate.fullName === "" ? "Enter their full name" : "Enter a first and last name";
	}
	if (!isValidEmail(candidate.emailAddress)) {
		errors.emailAddress = candidate.emailAddress === "" ? "Enter their email address" : "Enter a valid email address";
	}
	// Optional, so only an entered value can be wrong.
	if (candidate.phoneNumber !== "" && !isValidPhoneNumberWithTZCode(candidate.phoneNumber)) {
		errors.phoneNumber = "Use the format 255XXXXXXXXX";
	}

	return errors;
}

/**
 * Refer someone, then actually tell them.
 *
 * Sending the form is not the end of the task: the server emails the person an invitation, and the
 * points only move once they finish a resume. The previous modal closed on success and said neither
 * thing, so the work read as typing a stranger's details into a void. The second step hands over a
 * message to send, which is the part that decides whether the referral goes anywhere.
 */
function ReferFriendModal({ open, onClose }: ReferFriendModalProps) {
	const [candidate, setCandidate] = useState<Candidate>(EMPTY);
	// Errors appear per field only once it has been left. Validating on mount put two red messages
	// on an untouched empty form.
	const [touched, setTouched] = useState<Partial<Record<FieldName, boolean>>>({});
	const [referred, setReferred] = useState<Candidate | null>(null);
	const [copied, setCopied] = useState(false);
	// The server's reason for refusing, kept beside the form rather than in a toast that outlives the
	// modal and lands nowhere near the field that caused it.
	const [refusal, setRefusal] = useState<string | null>(null);

	const errors = validate(candidate);
	const isValid = Object.keys(errors).length === 0;

	const { mutate: refer, isPending } = useCreateReferral({
		onSuccess: () => {
			posthog.capture("referral_created");
			setRefusal(null);
			setReferred(candidate);
		},
		onError: (error: unknown) => {
			// A repeat referral answers 409, and the reason is almost always the email address — so it
			// belongs next to the form the reader is about to correct rather than in a toast.
			setRefusal(referralRefusalMessage(error, candidate.fullName));
			posthog.capture("referral_failed");
		},
	});

	const close = () => {
		setCandidate(EMPTY);
		setTouched({});
		setReferred(null);
		setCopied(false);
		setRefusal(null);
		onClose();
	};

	// Typing is the reader answering the refusal, so the refusal goes as soon as they start.
	const update = (field: FieldName, value: string) => {
		setRefusal(null);
		setCandidate((prev) => ({ ...prev, [field]: value }));
	};
	const errorFor = (field: FieldName) => (touched[field] ? errors[field] : undefined);

	const firstName = referred?.fullName.trim().split(/\s+/)[0] || "there";
	const message = referralInviteMessage(referred?.fullName);

	const copyMessage = async () => {
		await navigator.clipboard.writeText(message);
		setCopied(true);
		posthog.capture("referral_message_copied");
		setTimeout(() => setCopied(false), 2000);
	};

	return (
		<Dialog open={open} onOpenChange={(next) => !next && close()}>
			{/* The `sm:` prefix is load-bearing: the base content sets `sm:max-w-100`, which an unprefixed
			    width never beats above that breakpoint. */}
			<DialogContent className="sm:max-w-2xl">
				<DialogHeader>
					<DialogTitle className="font-plus-jakarta">
						{referred ? `Now tell ${firstName}` : "Refer a friend"}
					</DialogTitle>
				</DialogHeader>

				{referred ? (
					<div className="space-y-4">
						<Alert variant="success">
							<AlertDescription className="font-inter">
								We emailed an invitation to <strong className="font-medium">{referred.emailAddress}</strong>.
							</AlertDescription>
						</Alert>

						<p className="text-sm text-gray-600 font-inter leading-relaxed">
							An email is easy to miss. Send {firstName} a message too.
						</p>

						<div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
							<p className="text-sm text-gray-700 font-inter leading-relaxed">{message}</p>
						</div>

						<div className="flex flex-col gap-2 sm:flex-row">
							{/* WhatsApp's own green rather than the brand primary: this button leaves the product,
                  and looking like the app it opens is what says so before the label is read. */}
							<Button className={cn("flex-1", WHATSAPP_BUTTON)} asChild>
								<a
									href={whatsappLink(message, referred.phoneNumber)}
									target="_blank"
									rel="noopener noreferrer"
									onClick={() => posthog.capture("referral_whatsapp_opened")}
								>
									<WhatsAppIcon className="size-4" />
									<span className="ml-1.5">{referred.phoneNumber ? "Send on WhatsApp" : "Share on WhatsApp"}</span>
								</a>
							</Button>
							<Button variant="outlineSecondary" className="flex-1" onClick={copyMessage}>
								{copied ? <Check className="size-4" /> : <Copy className="size-4" />}
								<span className="ml-1.5">{copied ? "Copied" : "Copy message"}</span>
							</Button>
						</div>

						<Button variant="ghost" className="w-full" onClick={close}>
							Done
						</Button>
					</div>
				) : (
					<div className="space-y-4">
						<p className="text-sm text-gray-600 font-inter leading-relaxed">
							We email them an invitation to build a resume. You collect {REWARD_VALUE} points once their resume reaches{" "}
							{CLAIMABLE_COMPLETION_PCT}%.
						</p>

						{refusal && (
							<Alert variant="destructive">
								<AlertDescription className="font-inter">{refusal}</AlertDescription>
							</Alert>
						)}

						<div className="grid gap-4">
							<div className="space-y-1.5">
								<Label htmlFor="referral-name">Full name</Label>
								<Input
									id="referral-name"
									value={candidate.fullName}
									placeholder="John Doe"
									autoComplete="off"
									onChange={(event) => update("fullName", event.target.value)}
									onBlur={() => setTouched((prev) => ({ ...prev, fullName: true }))}
									aria-invalid={!!errorFor("fullName")}
								/>
								{errorFor("fullName") && <p className="text-xs text-red-600">{errorFor("fullName")}</p>}
							</div>

							<div className="space-y-1.5">
								<Label htmlFor="referral-email">Email address</Label>
								<Input
									id="referral-email"
									type="email"
									value={candidate.emailAddress}
									placeholder="john@example.com"
									autoComplete="off"
									onChange={(event) => update("emailAddress", event.target.value)}
									onBlur={() => setTouched((prev) => ({ ...prev, emailAddress: true }))}
									aria-invalid={!!errorFor("emailAddress")}
								/>
								{errorFor("emailAddress") && <p className="text-xs text-red-600">{errorFor("emailAddress")}</p>}
							</div>

							<div className="space-y-1.5">
								<Label htmlFor="referral-phone">
									Phone number <span className="font-normal text-gray-400">(optional)</span>
								</Label>
								<Input
									id="referral-phone"
									value={candidate.phoneNumber}
									placeholder="255712345678"
									autoComplete="off"
									onChange={(event) => update("phoneNumber", event.target.value)}
									onBlur={() => setTouched((prev) => ({ ...prev, phoneNumber: true }))}
									aria-invalid={!!errorFor("phoneNumber")}
								/>
								{errorFor("phoneNumber") ? (
									<p className="text-xs text-red-600">{errorFor("phoneNumber")}</p>
								) : (
									<p className="text-xs text-gray-500">Add it and we can open WhatsApp for you on the next step.</p>
								)}
							</div>
						</div>

						<div className="flex justify-end gap-2 pt-2">
							<Button variant="outlineSecondary" onClick={close}>
								Cancel
							</Button>
							<Button
								onClick={() => {
									setTouched({
										fullName: true,
										emailAddress: true,
										phoneNumber: true,
									});
									if (isValid) {
										refer({
											referredFullName: candidate.fullName,
											referredEmailAddress: candidate.emailAddress,
											referredPhoneNumber: candidate.phoneNumber,
										});
									}
								}}
								disabled={isPending}
							>
								{isPending && <Loader2 className="mr-1.5 size-4 animate-spin" />}
								Send invitation
							</Button>
						</div>
					</div>
				)}
			</DialogContent>
		</Dialog>
	);
}

export default ReferFriendModal;
